/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  clearSiteVisits,
  createSite as createSiteRecord,
  deleteSite as deleteSiteRecord,
  getCurrentSiteId,
  getSiteById,
  getSites,
  getSitesByUser,
  saveSites,
  setCurrentSiteId,
  updateSite as updateSiteRecord,
} from '../lib/store';
import { clearRemoteVisits, syncCurrentUser } from '../lib/cloud';
import type { Site, SiteStatus } from '../lib/types';

type SiteContextValue = {
  sites: Site[];
  currentSite: Site | null;
  setCurrentSite: (id: string) => void;
  refresh: () => void;
  createSite: (
    name: string,
    domain: string,
    extra?: { industry?: string; status?: SiteStatus }
  ) => Promise<Site>;
  updateSite: (id: string, patch: Partial<Pick<Site, 'name' | 'domain' | 'status' | 'industry'>>) => Promise<void>;
  clearVisits: (id: string) => void;
  deleteSite: (id: string) => Promise<void>;
};

const SiteContext = createContext<SiteContextValue | null>(null);

export function SiteProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    if (!user) {
      setSites([]);
      setCurrentId(null);
      return;
    }
    const next = getSitesByUser(user.id);
    setSites(next);
    const stored = getCurrentSiteId();
    const valid = next.find((s) => s.id === stored) || next[0] || null;
    setCurrentId(valid?.id ?? null);
    if (valid) setCurrentSiteId(valid.id);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<SiteContextValue>(
    () => ({
      sites,
      currentSite: sites.find((s) => s.id === currentId) || null,
      setCurrentSite(id: string) {
        setCurrentSiteId(id);
        setCurrentId(id);
      },
      refresh,
      async createSite(name, domain, extra) {
        if (!user) throw new Error('请先登录');
        const site = createSiteRecord(user.id, name, domain, extra);
        try {
          await syncCurrentUser(user.id);
        } catch (err) {
          deleteSiteRecord(site.id);
          refresh();
          throw err instanceof Error ? err : new Error('同步站点失败');
        }
        refresh();
        return site;
      },
      async updateSite(id, patch) {
        if (!user) throw new Error('请先登录');
        const prev = getSiteById(id);
        if (!prev) throw new Error('站点不存在');
        updateSiteRecord(id, patch);
        try {
          await syncCurrentUser(user.id);
        } catch (err) {
          updateSiteRecord(id, {
            name: prev.name,
            domain: prev.domain,
            status: prev.status,
            industry: prev.industry,
          });
          refresh();
          throw err instanceof Error ? err : new Error('同步站点失败');
        }
        refresh();
      },
      clearVisits(id) {
        const site = getSiteById(id);
        clearSiteVisits(id);
        if (user && site) {
          clearRemoteVisits(user.apiToken, site.trackingCode).catch(() => undefined);
        }
        refresh();
      },
      async deleteSite(id: string) {
        if (!user) return;
        const prev = getSiteById(id);
        if (!prev) return;
        deleteSiteRecord(id);
        refresh();
        try {
          await syncCurrentUser(user.id, { replace: true });
        } catch (err) {
          if (!getSiteById(prev.id)) saveSites([...getSites(), prev]);
          refresh();
          throw err instanceof Error ? err : new Error('同步站点失败');
        }
      },
    }),
    [sites, currentId, user, refresh]
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSites() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSites must be used within SiteProvider');
  return ctx;
}
